import { useMemo } from 'react'
import { styled } from 'styled-components'
import StreetViewPanorama from '~/components/streetViewPanorama'
import Loader from '~/prix/react/components/loader'
import useAction from '~/prix/react/hooks/action'
import geoReverseGeocodingDefinition from '~/packages/geo/reverseGeocoding/geoReverseGeocoding.definition'
import ErrorImage from '~/assets/error.svg'

export const MapContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 100%;
  height: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    min-height: 280px;
    height: 280px;
    width: 100%;
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.padding(1)};
  width: 100%;
  height: 100%;
`

const ErrorTitle = styled.h2`
  font-size: 40px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
`

const ErrorText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const StreetViewContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  background-color: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 20px;
  overflow: hidden;
`

export interface ShowStreetViewProps {
  coordinates?: {
    lat: number
    lng: number
  }
}

export default function ShowStreetView({ coordinates }: ShowStreetViewProps) {
  const reverseGeocoding = useAction(
    geoReverseGeocodingDefinition,
    {
      longitude: coordinates?.lng ?? 0,
      latitude: coordinates?.lat ?? 0,
      include: ['pointOnStreet', 'pointOnStreetHeading'],
    },
    [coordinates],
    { autoLoad: Boolean(coordinates) },
  )

  const streetViewPosition = useMemo(() => {
    if (!coordinates || !reverseGeocoding.result?.results) {
      return null
    }

    return {
      lat: reverseGeocoding?.result?.results[0]?.pointOnStreet?.coordinates[1] as number,
      lng: reverseGeocoding?.result?.results[0]?.pointOnStreet?.coordinates[0] as number,
      heading: reverseGeocoding?.result?.results[0]?.pointOnStreetHeading as number,
    }
  }, [reverseGeocoding.result, coordinates])

  return (
    <MapContainer className='streetmap-container'>
      {/* Em caso de erro, exibe uma mensagem de erro */}
      {(reverseGeocoding?.result?.results?.length === 0 || reverseGeocoding.error) && (
        <ErrorContainer>
          <ErrorImage width={300} height={350} />
          <ErrorTitle>Oops!</ErrorTitle>
          <ErrorText>Não foi possível obter a posição do endereço</ErrorText>
        </ErrorContainer>
      )}
      {/* Em caso de loading, exibe o loader */}
      {reverseGeocoding.isLoading && <Loader />}
      {/* Em caso de sucesso, exibe o StreetView */}
      {streetViewPosition && !reverseGeocoding.isLoading && (
        <StreetViewContainer>
          <StreetViewPanorama
            position={streetViewPosition}
            markerPosition={streetViewPosition}
            hideWhenNotAvailable={false}
          />
        </StreetViewContainer>
      )}
    </MapContainer>
  )
}
